import { useState } from "react";
import { Info } from "lucide-react";
import { T } from "../../lib/testids";
import { estimateLabel, fmtMoneyObj, fmtPct } from "../../lib/format";

/**
 * Renders a monetary figure derived from an Estimate. Every visible
 * money value in Commercial Operator that is not a HARD close MUST use
 * this component — nunca `fmtMoneyObj` directo (bug real encontrado en QA:
 * OpportunityRow.jsx mostraba "USD 0" crudo en la tabla mientras el panel
 * de detalle, que sí usaba este componente, mostraba "Lo estoy
 * calculando" para la misma oportunidad — violación de la Regla #3).
 *
 * Props:
 *   estimate: Estimate | null
 *   size: "sm" | "md" | "lg" | "xl"
 *   tone: "neutral" | "amber" | "success"
 *   compact: sin línea de etiqueta ni ícono de auditoría — para tablas
 *     densas donde el encabezado de columna ya da el contexto.
 */
export default function AuditableFigure({ estimate, size = "md", tone = "neutral", label, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!estimate) {
    return <span className="text-zinc-600 font-mono">—</span>;
  }
  // Un "$0" nunca comunica nada útil — o no hay suficientes casos todavía,
  // o la cifra sigue en análisis. Ninguna de las dos es lo mismo que "cero
  // dinero", así que nunca se muestra como si lo fuera.
  if (estimate.value.amount === 0) {
    const waitingText = estimate.sampleSize === 0 ? "Todavía no hay suficiente historial" : "Lo estoy calculando";
    if (compact) {
      return (
        <span
          className="font-mono text-[11px] text-zinc-500 italic"
          data-testid={T.auditableFigure}
        >
          {waitingText}
        </span>
      );
    }
    return (
      <span className="inline-block" data-testid={T.auditableFigure}>
        <span className="font-display font-bold text-zinc-500 italic text-lg">{waitingText}</span>
        <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-600 font-mono mt-1">
          {label ? `${label} · ` : ""}{estimateLabel(estimate.label)}
        </div>
      </span>
    );
  }
  if (compact) {
    return (
      <span
        className="font-mono text-zinc-100 tabular-nums font-semibold text-[13px]"
        data-testid={T.auditableFigure}
      >
        {fmtMoneyObj(estimate.value)}
      </span>
    );
  }
  const sizeCls = {
    sm: "text-lg",
    md: "text-2xl",
    lg: "text-4xl",
    xl: "text-6xl lg:text-7xl",
  }[size];
  const toneCls = {
    neutral: "text-zinc-100",
    amber: "text-amber-400",
    success: "text-emerald-400",
  }[tone];

  return (
    <span className="relative inline-block" data-testid={T.auditableFigure}>
      <span className="inline-flex items-baseline gap-2">
        <span
          className={`font-display font-black tracking-tight tabular-nums ${sizeCls} ${toneCls}`}
        >
          {fmtMoneyObj(estimate.value)}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-zinc-500 hover:text-amber-400 hover:scale-110 transition-all"
          aria-label="Ver cómo se calcula esta cifra"
        >
          <Info size={13} />
        </button>
      </span>
      {label ? (
        <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-mono mt-1">
          {label} · {estimateLabel(estimate.label)}
        </div>
      ) : (
        <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-mono mt-1">
          {estimateLabel(estimate.label)}
        </div>
      )}

      {open && (
        <div
          data-testid={T.auditableFigurePopover}
          className="absolute z-30 mt-2 w-80 rounded-2xl bg-[#131a2c] p-5 text-left shadow-[0_20px_50px_-16px_rgba(0,0,0,0.6)]"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="text-[9px] uppercase tracking-[0.22em] text-amber-400 font-mono mb-2">
            ¿Cómo se calcula?
          </div>
          <div className="text-xs text-zinc-300 leading-relaxed mb-3">
            {estimate.rationale}
          </div>
          <div className="grid grid-cols-2 gap-3 text-[10px] font-mono uppercase tracking-[0.15em]">
            <div>
              <div className="text-zinc-500">Confianza</div>
              <div className="text-zinc-100 font-bold">
                {fmtPct(estimate.confidence)}
              </div>
            </div>
            <div>
              <div className="text-zinc-500">Casos históricos</div>
              <div className="text-zinc-100 font-bold">{estimate.sampleSize}</div>
            </div>
            <div>
              <div className="text-zinc-500">Margen de error</div>
              <div className="text-zinc-100 font-bold">±{estimate.errorMarginPct}%</div>
            </div>
            <div>
              <div className="text-zinc-500">Recalculada</div>
              <div className="text-zinc-100 font-bold">
                {new Date(estimate.lastRecalculatedAt).toLocaleDateString("es-MX")}
              </div>
            </div>
          </div>
          <div className="text-[9px] text-zinc-600 mt-3 leading-relaxed border-t border-white/[0.07] pt-2">
            Estimación derivada de tu propia cartera. No es un hecho contable —
            se ajusta con cada outcome registrado.
          </div>
        </div>
      )}
    </span>
  );
}
